import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Form, Input, InputNumber, Button, Space, Typography, DatePicker, Select, Spin } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  useCreateCustomerCreditNote,
  useCustomerCreditNote,
  useUpdateCustomerCreditNote,
} from '@/hooks/useCustomerCreditNote';
import { useActiveCustomerOptions } from '@/hooks/useCustomers';

const { Title } = Typography;

const CustomerCreditNoteFormPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = Boolean(id);
  const createMutation = useCreateCustomerCreditNote();
  const updateMutation = useUpdateCustomerCreditNote();
  const { options: customerOptions = [] } = useActiveCustomerOptions();
  const { data: existing, isLoading: loadingExisting } = useCustomerCreditNote(isEdit ? id! : null);

  useEffect(() => {
    if (!isEdit || !existing) return;
    form.setFieldsValue({
      customer_id: existing.customer_id,
      credit_note_date: existing.credit_note_date ? dayjs(existing.credit_note_date) : undefined,
      total_amount: Number(existing.total_amount),
      reason: existing.reason ?? undefined,
    });
  }, [isEdit, existing, form]);

  const onFinish = async (values: {
    customer_id: string;
    credit_note_date: dayjs.Dayjs;
    total_amount: number;
    reason?: string;
  }) => {
    const credit_note_date = values.credit_note_date
      ? dayjs(values.credit_note_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD');
    const dto = {
      customer_id: values.customer_id,
      credit_note_date,
      total_amount: Number(values.total_amount),
      reason: values.reason || undefined,
      original_sales_invoice_id: existing?.original_sales_invoice_id || undefined,
    };
    try {
      if (isEdit && id) {
        await updateMutation.mutateAsync({ id, dto });
        navigate(`/fin/credit-notes/${id}`);
      } else {
        const created = await createMutation.mutateAsync(dto);
        navigate(`/fin/credit-notes/${created.id}`);
      }
    } catch {
      /* mutation onError */
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/credit-notes')} style={{ marginBottom: 16 }}>
        {t('backToCreditNotes')}
      </Button>
      <Title level={4}>{isEdit ? t('editCustomerCreditNote') : t('newCustomerCreditNote')}</Title>
      <Card style={{ maxWidth: 600 }}>
        {isEdit && loadingExisting ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={isEdit ? undefined : { credit_note_date: dayjs() }}
          >
            <Form.Item
              name="customer_id"
              label={t('customer')}
              rules={[{ required: true, message: t('selectCustomer') }]}
            >
              <Select
                placeholder={t('selectCustomer')}
                allowClear
                showSearch
                optionFilterProp="label"
                options={customerOptions}
              />
            </Form.Item>
            <Form.Item name="credit_note_date" label={t('creditNoteDate')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="total_amount"
              label={t('totalAmount')}
              rules={[{ required: true, message: t('enterAmount') }, { type: 'number', min: 0.01 }]}
            >
              <InputNumber min={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="reason" label={t('reason')}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                  {isEdit ? t('saveChanges') : t('createDraft')}
                </Button>
                <Button onClick={() => navigate('/fin/credit-notes')}>{tc('cancel')}</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default CustomerCreditNoteFormPage;
